import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import {
  $TableTree,
  $Button,
  $Tag,
  $Popconfirm,
  $Skeleton,
  $Popover,
  $Select,
  $Search,
  $Drawer,
  $Affix,
  $PageHeader,
  $Divider,
  $Tooltip,
} from "us.common/components";
import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
} from "us.icons";
import { handleGoBack } from "us.helper/utility";
import { useHistory } from "react-router-dom";
import { Formik } from "formik";
import { roleManagementActions } from "us.common/actions";
import { IRoleSummaryWithExtendedFields } from "us.common/interfaces";
import { filterRoleByActivityStatus, searchRoleByName } from "./Functions";
import { RoleDetails } from "./Components";
import { ChangeRoleState } from "./Repository";
import { RootState } from "us.helper/types";

const { roles, role } = roleManagementActions;

const RoleManagement: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    getRoles,
    roles,
    drawer,
    manageDrawer,
    resetForm,
    deleteRole,
    changeRoleStatus,
  } = props;

  const { data, isLoading } = roles;
  const { title, visible } = drawer;

  const [dataSource, setDataSource] =
    useState<Array<IRoleSummaryWithExtendedFields>>(data);
  const [popVisible, setPopVisible] = useState<number>(-1);

  useEffect(() => {
    getRoles && getRoles({});
  }, []);

  useEffect(() => {
    if (data.length > 0) {
      setDataSource(data);
    }
  }, [data]);

  /**
   * @function
   * @description handle search by role name
   * @param {string} searchText search value
   * @param {string} filter filter value
   */
  const handleSearch = (searchText: string, filter: string) => {
    try {
      setDataSource(searchRoleByName(data, searchText));
    } catch (error) {
      setDataSource(data);
    }
  };

  const onOpenDrawer = (title: string, isNew: boolean, roleId: number) => {
    manageDrawer &&
      manageDrawer({
        title: t(`US.COMMON:MANAGE_ROLES.${title}`),
        isNew,
        visible: true,
        roleId,
      });
  };

  /**
   * @function
   * @description handle drawer close event
   */
  const onClose = () => {
    manageDrawer &&
      manageDrawer({
        title: "",
        isNew: true,
        visible: false,
        roleId: 0,
      });
    resetForm && resetForm({});
  };

  /**
   * @function
   * @description handle state changes
   * @param {AnyARecord}record
   */
  const handleStateChange = (record: any) => {
    try {
      const request = ChangeRoleState.call(record);
      changeRoleStatus && changeRoleStatus(request);
    } catch (error) {}
  };

  const content = (record: any) => (
    <div className="table-more-content">
      <div className="d-flex  flex-row more-item">
        <div className="p-1">
          <EditOutlined />
        </div>
        <div
          className="p-1"
          onClick={() => {
            setPopVisible(-1);
            onOpenDrawer("EDIT_ROLE", false, record?.roleId);
          }}
        >
          {t("COMMON.EDIT")}
        </div>
      </div>
      <$Popconfirm
        title={t("US.COMMON:MANAGE_USER.SURE_TO_DELETE_?")}
        onConfirm={() => {
          setPopVisible(-1);
          deleteRole && deleteRole(record);
        }}
      >
        <div className="d-flex mb-2 flex-row more-item text-error">
          <div className="p-1">
            <DeleteOutlined />
          </div>
          <div className="p-1">{t("COMMON.DELETE")}</div>
        </div>
      </$Popconfirm>
      <$Divider className="popup-divider my-2" />

      <$Popconfirm
        title={`${t("US.COMMON:MANAGE_ROLES.ACTIVESTATECONFIRM")} ${
          record.activeState
            ? t("US.COMMON:MANAGE_ROLES.DEACTIVATE")
            : t("US.COMMON:MANAGE_ROLES.ACTIVATE")
        }?`}
        onConfirm={() => {
          setPopVisible(-1);
          handleStateChange(record);
        }}
        okText={t("US.COLLECTION.COMMON:COMMON.YES")}
        cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
      >
        <div className="d-flex flex-column">
          <$Button type="primary" className="mb-1" size="small">
            {record.activeState
              ? t("US.COMMON:MANAGE_ROLES.DEACTIVATE")
              : t("US.COMMON:MANAGE_ROLES.ACTIVATE")}
          </$Button>
        </div>
      </$Popconfirm>
    </div>
  );

  const columns: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: "50px",
      customRenderChild: (text: any, record: any) => {
        return (
          <$Popover
            trigger="click"
            placement="rightTop"
            content={content(record)}
            destroyTooltipOnHide
            visible={record.roleId == popVisible}
            onVisibleChange={(visible: boolean) => {
              setPopVisible(visible ? record.roleId : -1);
            }}
          >
            <$Button icon={<MoreOutlined />} size="small" />
          </$Popover>
        );
      },
    },
    {
      title: t("US.COMMON:MANAGE_ROLES.ROLE_NAME"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COMMON:MANAGE_ROLES.STATE"),
      dataIndex: "activeStateString",
      key: "activeStateString",
      width: 200,
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
      customRenderChild: (text: any, record: any) => {
        return (
          <>
            {record.activeState && (
              <$Tag className="tag-status-active">
                {t("US.COMMON:MANAGE_ROLES.ACTIVE")}
              </$Tag>
            )}
            {!record.activeState && (
              <$Tag className="tag-status-deactive">
                {t("US.COMMON:MANAGE_ROLES.INACTIVE")}
              </$Tag>
            )}
          </>
        );
      },
    },
    {
      title: t("US.COMMON:MANAGE_ROLES.DESCRIPTION"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
    },
  ];

  return (
    <Formik
      enableReinitialize
      initialValues={{ status: "active" }}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }: any) => (
        <div className="space-content">
          <$Affix offsetTop={48}>
            <div className="page-header header-border pr-0">
              <div className="d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <$PageHeader
                    className="px-0"
                    title={t("US.COMMON:MANAGE_ROLES.MANAGE_ROLES")}
                    onBack={() => handleGoBack(history)}
                  />
                  <>
                    <$Divider className="bui-devider" type="vertical" />
                    <$Button
                      type="dashed"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => onOpenDrawer("NEW_ROLE", true, 0)}
                    >
                      {t("US.COMMON:MANAGE_ROLES.NEW_ROLE")}
                    </$Button>
                  </>
                </div>
                <div className="ml-5">
                  <$Search
                    size="small"
                    name="searchRole"
                    allowClear={true}
                    placeholder={t("US.COMMON:MANAGE_ROLES.SEARCH_ROLES")}
                    onChange={(e: any) =>
                      handleSearch(e.target.value, values.status)
                    }
                    style={{
                      width: 200,
                    }}
                  />
                  <$Divider type="vertical" />
                  <$Tooltip title={t("COMMON.STATUS")}>
                    <$Select
                      placeholder={t("COMMON.STATUS")}
                      size="small"
                      name="status"
                      onSelect={(status: string) =>
                        setFieldValue("status", status)
                      }
                      style={{
                        width: 120,
                      }}
                      allOption={false}
                      options={[
                        {
                          label: t("COMMON.ALL"),
                          value: "all",
                        },
                        {
                          label: t("COMMON.ACTIVE"),
                          value: "active",
                        },
                        {
                          label: t("COMMON.INACTIVE"),
                          value: "deactivated",
                        },
                      ]}
                      defaultValue={"active"}
                    />
                  </$Tooltip>
                </div>
              </div>
            </div>
          </$Affix>
          <$Skeleton loading={isLoading} active paragraph={{ rows: 2 }}>
            <$TableTree
              rowKey="roleId"
              data={filterRoleByActivityStatus(dataSource, values.status)}
              columns={columns}
              size="small"
              className="mt-3"
              onSort={(sortData, dataSource) => sortData(dataSource)}
              onFilter={(searchData, dataSource) => searchData(dataSource)}
              filterOnType={true}
              resetOnSourceChange={true}
              bordered
              pagination={{
                defaultPageSize: 20,
              }}
              scroll={{
                x: 1200,
                y: "calc(100vh - 215px)",
              }}
              onRow={(record: any) => {
                return {
                  onDoubleClick: () => {
                    onOpenDrawer("EDIT_ROLE", false, record?.roleId);
                  },
                };
              }}
            />
          </$Skeleton>
          <$Drawer
            title={title}
            width={800}
            visible={visible}
            onClose={onClose}
            destroyOnClose
          >
            <RoleDetails />
          </$Drawer>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { roleManagement } = state;
  const { roles, drawer } = roleManagement;
  return {
    roles,
    drawer,
  };
};
const mapDispatchToProps = {
  getRoles: roles.get,
  manageDrawer: role.openDrawer,
  resetForm: role.reset,
  deleteRole: role.delete,
  changeRoleStatus: role.activateOrDeactivate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(RoleManagement);
